import { Bell } from "lucide-react";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const toISODate = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getTourChildren = (tour) => {
  if (tour.children?.length) return tour.children;
  if (tour.childFirstName) return [{ firstName: tour.childFirstName, suggestedRoomId: tour.suggestedRoomId }];
  return [];
};

const childNamesSuffix = (tour) => {
  const names = getTourChildren(tour).map((c) => c.firstName).filter(Boolean);
  return names.length ? ` · ${names.join(", ")}` : "";
};

const fmtTime = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
};

const TourHoverCard = ({ tour, roomsById }) => {
  const children = getTourChildren(tour);
  return (
    <div
      className="hidden group-hover:block absolute z-50 left-0 top-full mt-1 w-60 rounded-lg bg-slate-800 text-white text-[11px] p-2.5 shadow-xl pointer-events-none"
      data-testid={`tour-hover-card-${tour.id}`}
    >
      <p className="font-semibold">{tour.parentName}</p>
      {children.length > 0 && (
        <p className="text-slate-200 mt-1">
          {children.map((c, i) => {
            const name = c.firstName || `Child ${i + 1}`;
            const room = c.suggestedRoomId ? roomsById?.[c.suggestedRoomId]?.name : null;
            return room ? `${name} → ${room}` : name;
          }).join(" · ")}
        </p>
      )}
      {tour.parentPhone && <p className="text-slate-300 mt-1">{tour.parentPhone}</p>}
      {tour.notes && <p className="text-slate-400 mt-1.5 italic line-clamp-2">{tour.notes}</p>}
    </div>
  );
};

const buildMonthGrid = (anchor) => {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
};

const buildWeekDays = (anchor) => {
  const start = new Date(anchor);
  start.setDate(anchor.getDate() - anchor.getDay());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
};

const TourPill = ({ tour, onClick, roomsById }) => {
  const hasReminder = tour.parentReminder?.enabled || tour.staffAlert?.enabled;
  const room = roomsById[tour.selectedRoomId] || roomsById[tour.suggestedRoomId];
  return (
    <div className="group relative mb-1">
      <button
        onClick={() => onClick(tour)}
        data-testid={`tour-pill-${tour.id}`}
        className="w-full text-left rounded-md bg-[#EEF1F4] hover:bg-[#E3E8EC] border border-slate-200/70 px-2 py-1 transition-colors"
      >
        <div className="flex items-center gap-1 text-[11px] font-medium text-slate-700">
          <span>{fmtTime(tour.startTime)}</span>
          {hasReminder && <Bell className="w-2.5 h-2.5 text-slate-400" />}
        </div>
        <div className="text-[11px] text-slate-600 truncate">
          {tour.parentName}{childNamesSuffix(tour)}
        </div>
        {room && <div className="text-[10px] text-slate-400 truncate">{room.name}</div>}
      </button>
      <TourHoverCard tour={tour} roomsById={roomsById} />
    </div>
  );
};

// A planned withdrawal is an all-day marker (not time-scoped like a tour) — visually distinct
// (dusty-red) so a director scanning the calendar never confuses a "last day" with a tour.
const WithdrawalChip = ({ withdrawal, onClick }) => (
  <button
    onClick={() => onClick(withdrawal)}
    data-testid={`withdrawal-pill-${withdrawal.id}`}
    className="w-full text-left rounded-md bg-[#C97B7B]/10 hover:bg-[#C97B7B]/20 border border-[#C97B7B]/30 px-2 py-1 mb-1 transition-colors"
  >
    <div className="text-[11px] font-medium text-[#B85C5C] truncate">Last Day: {withdrawal.childName}</div>
  </button>
);

const formatHourLabel = (h) => {
  const period = h < 12 ? "AM" : "PM";
  const disp = h % 12 === 0 ? 12 : h % 12;
  return `${disp} ${period}`;
};

// Default business-hours range; widens automatically if a tour falls outside it.
const DEFAULT_START_HOUR = 7;
const DEFAULT_END_HOUR = 18;

const buildHourRange = (weekDays, toursByDate) => {
  let min = DEFAULT_START_HOUR, max = DEFAULT_END_HOUR;
  weekDays.forEach((d) => {
    (toursByDate[toISODate(d)] || []).forEach((t) => {
      const h = parseInt((t.startTime || "0:0").split(":")[0], 10);
      if (!Number.isNaN(h)) {
        if (h < min) min = h;
        if (h > max) max = h;
      }
    });
  });
  return { min, max };
};

const TIME_PILL_CLASS = "block w-full text-left rounded-md bg-[#EEF1F4] hover:bg-[#E3E8EC] border border-slate-200/70 px-1.5 py-0.5 transition-colors overflow-hidden";

const TimedTourPill = ({ tour, onClick, roomsById, top }) => {
  const hasReminder = tour.parentReminder?.enabled || tour.staffAlert?.enabled;
  const room = roomsById[tour.selectedRoomId] || roomsById[tour.suggestedRoomId];
  return (
    <div className="group absolute left-1 right-1 z-10" style={{ top }}>
      <button onClick={() => onClick(tour)} data-testid={`tour-pill-${tour.id}`} className={TIME_PILL_CLASS}>
        <div className="flex items-center gap-1 text-[10px] font-medium text-slate-700">
          <span>{fmtTime(tour.startTime)}</span>
          {hasReminder && <Bell className="w-2.5 h-2.5 text-slate-400 shrink-0" />}
        </div>
        <div className="text-[10px] text-slate-600 truncate">
          {tour.parentName}{childNamesSuffix(tour)}
        </div>
        {room && <div className="text-[9px] text-slate-400 truncate">{room.name}</div>}
      </button>
      <TourHoverCard tour={tour} roomsById={roomsById} />
    </div>
  );
};

const ROW_H = 56; // px per hour — drives the minute-proportional top offset below

const timeToMinutes = (t) => {
  const [h, m] = String(t || "0:0").split(":").map((n) => parseInt(n, 10) || 0);
  return h * 60 + m;
};

const WeekTimeGrid = ({ weekDays, toursByDate, withdrawalsByDate, today, onSelectTour, onSelectWithdrawal, roomsById }) => {
  const { min, max } = buildHourRange(weekDays, toursByDate);
  const hours = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  const gridHeight = hours.length * ROW_H;
  const rangeStartMin = min * 60;
  const hasWithdrawals = weekDays.some((d) => (withdrawalsByDate[toISODate(d)] || []).length > 0);

  return (
    // overflow-x-auto + a fixed min-width lets the 8-column grid (gutter + 7 days) scroll
    // horizontally on narrow/mobile viewports instead of squashing illegibly.
    <div className="overflow-x-auto" data-testid="planner-week-grid">
      <div style={{ minWidth: 720 }}>
        <div className="grid border border-slate-200 rounded-t-lg overflow-hidden" style={{ gridTemplateColumns: `56px repeat(7, minmax(0,1fr))` }}>
          <div className="bg-slate-50 border-r border-slate-200" />
          {weekDays.map((d) => {
            const iso = toISODate(d);
            return (
              <div key={iso} className={`text-center py-2 ${iso === today ? "bg-[#4C8C8C]/[0.06]" : "bg-slate-50"}`}>
                <div className="text-xs font-semibold text-slate-500">{WEEKDAYS[d.getDay()]}</div>
                <div className="text-[11px] text-slate-400">{d.getDate()}</div>
              </div>
            );
          })}
        </div>
        {hasWithdrawals && (
          <div className="grid border-l border-r border-slate-200" style={{ gridTemplateColumns: `56px repeat(7, minmax(0,1fr))` }} data-testid="planner-week-withdrawals-row">
            <div className="bg-slate-50 border-r border-slate-200" />
            {weekDays.map((d) => {
              const iso = toISODate(d);
              const dayWithdrawals = withdrawalsByDate[iso] || [];
              return (
                <div key={iso} className="px-1 pt-1 bg-white border-r border-slate-100 last:border-r-0">
                  {dayWithdrawals.map((w) => <WithdrawalChip key={w.id} withdrawal={w} onClick={onSelectWithdrawal} />)}
                </div>
              );
            })}
          </div>
        )}
        <div className="grid border border-t-0 border-slate-200 rounded-b-lg overflow-hidden" style={{ gridTemplateColumns: `56px repeat(7, minmax(0,1fr))` }}>
          <div className="relative border-r border-slate-200" style={{ height: gridHeight }}>
            {hours.map((h, i) => (
              <div key={h} className="absolute right-1.5 text-[10px] text-slate-400" style={{ top: i * ROW_H - 6 }}>
                {formatHourLabel(h)}
              </div>
            ))}
            {hours.map((h, i) => <div key={`l-${h}`} className="absolute left-0 right-0 border-t border-slate-100" style={{ top: i * ROW_H }} />)}
          </div>
          {weekDays.map((d) => {
            const iso = toISODate(d);
            const dayTours = toursByDate[iso] || [];
            return (
              <div key={iso} className={`relative border-r border-slate-100 last:border-r-0 ${iso === today ? "bg-[#4C8C8C]/[0.03]" : ""}`} style={{ height: gridHeight }}>
                {hours.map((h, i) => <div key={h} className="absolute left-0 right-0 border-t border-slate-100" style={{ top: i * ROW_H }} />)}
                {dayTours.map((t) => {
                  const minutesFromStart = timeToMinutes(t.startTime) - rangeStartMin;
                  const top = Math.max(0, Math.min(gridHeight - 30, (minutesFromStart / 60) * ROW_H));
                  return <TimedTourPill key={t.id} tour={t} onClick={onSelectTour} roomsById={roomsById} top={top} />;
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export const PlannerCalendar = ({ view, anchorDate, onNavigate, tours, withdrawals = [], roomsById, onSelectTour, onSelectWithdrawal }) => {
  const toursByDate = {};
  tours.forEach((t) => { (toursByDate[t.date] = toursByDate[t.date] || []).push(t); });
  Object.values(toursByDate).forEach((arr) => arr.sort((a, b) => a.startTime.localeCompare(b.startTime)));

  const withdrawalsByDate = {};
  withdrawals.forEach((w) => { (withdrawalsByDate[w.plannedWithdrawalDate] = withdrawalsByDate[w.plannedWithdrawalDate] || []).push(w); });

  const today = toISODate(new Date());

  if (view === "week") {
    const weekDays = buildWeekDays(anchorDate);
    return (
      <WeekTimeGrid
        weekDays={weekDays}
        toursByDate={toursByDate}
        withdrawalsByDate={withdrawalsByDate}
        today={today}
        onSelectTour={onSelectTour}
        onSelectWithdrawal={onSelectWithdrawal}
        roomsById={roomsById}
      />
    );
  }

  const monthDays = buildMonthGrid(anchorDate);
  return (
    <div data-testid="planner-month-grid">
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((w) => <div key={w} className="text-xs font-semibold text-slate-400 text-center py-1">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {monthDays.map((d) => {
          const iso = toISODate(d);
          const inMonth = d.getMonth() === anchorDate.getMonth();
          const dayTours = toursByDate[iso] || [];
          const dayWithdrawals = withdrawalsByDate[iso] || [];
          return (
            <div
              key={iso}
              className={`rounded-lg border p-1.5 min-h-[92px] ${!inMonth ? "bg-slate-50/60 border-slate-100" : iso === today ? "border-[#4C8C8C]/50 bg-[#4C8C8C]/[0.04]" : "border-slate-200 bg-white"}`}
            >
              <div className={`text-[11px] mb-1 ${inMonth ? "text-slate-500" : "text-slate-300"}`}>{d.getDate()}</div>
              {dayTours.map((t) => <TourPill key={t.id} tour={t} onClick={onSelectTour} roomsById={roomsById} />)}
              {dayWithdrawals.map((w) => <WithdrawalChip key={w.id} withdrawal={w} onClick={onSelectWithdrawal} />)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const navigateDate = (anchorDate, view, dir) => {
  const d = new Date(anchorDate);
  if (view === "week") d.setDate(d.getDate() + dir * 7);
  else d.setMonth(d.getMonth() + dir);
  return d;
};

export const formatRangeLabel = (anchorDate, view) => {
  if (view === "month") return anchorDate.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const days = buildWeekDays(anchorDate);
  const start = days[0], end = days[6];
  const opts = { month: "short", day: "numeric" };
  return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}, ${end.getFullYear()}`;
};

export { toISODate };
